import { Text, useColorModeValue } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { forwardRef, useCallback, useMemo, useState } from "react";

import { ListType } from "@/common/components/MultiSelector/MultiSelector";
import { MultiSelectorPopover } from "@/common/components/MultiSelector/MultiSelectorPopover";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import { useUserNamesData } from "@/common/data/useUserNamesData";
import { useUsersSearch } from "@/common/data/useUsers";
import { GroupSummary } from "@/feature/components/AccessSummary";
import { UserName } from "@reflag/shared/userAPI";

import { RuleSimpleSelection } from "./RuleSimpleSelectionProps";
import { SelectorProps } from "./RuleSimpleSelectorTypes";

type RuleSimpleUserSelectorProps = SelectorProps & {
  envId?: string;
  summaryCount?: number;
  summaryLogoCount?: number;
  alwaysShowSummary?: boolean;
  allowReadOnlyOpen?: boolean;
};

export const RuleSimpleUserSelector = forwardRef<
  HTMLButtonElement,
  RuleSimpleUserSelectorProps
>(
  (
    {
      value,
      isReadOnly,
      envId: envIdOverride,
      summaryCount = 9,
      summaryLogoCount = 3,
      alwaysShowSummary = false,
      allowReadOnlyOpen = false,
      onChange: onChangeProp,
    },
    _ref,
  ) => {
    const { appId, envId: currentEnvId } = useEnvironment();
    const envId = envIdOverride ?? currentEnvId;
    const queryClient = useQueryClient();
    const [searchValue, setSearchValue] = useState("");
    const [listType, setListType] = useState<ListType>(
      isReadOnly ? "selected" : "all",
    );

    const {
      data: userNames = [],
      isFetching: isFetchingUserNames,
      isPlaceholderData: isPlaceholderDataUserNames,
    } = useUserNamesData(
      isReadOnly ? value?.slice(0, summaryCount) : value,
      envId,
    );

    const isNamesLoading = isFetchingUserNames && isPlaceholderDataUserNames;

    const avatarBackgroundColor = useColorModeValue(`gray.50`, `gray.750`);

    const summary = useMemo(() => {
      if (value && (alwaysShowSummary || value.length > summaryCount)) {
        return {
          slotType: "summary",
          type: "user",
          samples: userNames.slice(0, summaryLogoCount),
          count: value.length,
        };
      }
      return null;
    }, [alwaysShowSummary, userNames, summaryLogoCount, summaryCount, value]);

    // todo: remove unknown users when useUsersSearch supports it
    const unknownUsers = useMemo(() => {
      return userNames.filter(
        (u) => u.isUnknown && (!searchValue || u.id.includes(searchValue)),
      );
    }, [userNames, searchValue]);

    const {
      data: users = [],
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useUsersSearch(
      searchValue,
      {
        filter:
          listType === "selected"
            ? {
                type: "userAttribute",
                operator: "ANY_OF",
                field: "id",
                values: value,
              }
            : undefined,
      },
      undefined,
      envId,
    );

    const suggestions = useMemo(() => {
      return [...unknownUsers, ...users];
    }, [unknownUsers, users]);

    const onChange = useCallback(
      (userIds: string[], meta?: UserName[]) => {
        if (!onChangeProp) return;

        if (meta?.length) {
          // Get the current list of items by ids
          const current = queryClient.getQueryData<UserName[]>(
            commonQueryKeys.userNames(appId, envId, value),
          );

          // Update the list of items by ids
          queryClient.setQueryData<UserName[]>(
            commonQueryKeys.userNames(appId, envId, userIds),
            () => {
              return userIds.map((id) => {
                const metaItem = meta?.find((c: UserName) => c.id === id);
                const currentItem = current?.find((c: UserName) => c.id === id);

                return (
                  metaItem ??
                  currentItem ?? {
                    id,
                    name: null,
                    avatarUrl: null,
                    email: null,
                    isUnknown: true,
                  }
                );
              });
            },
          );
        } else {
          queryClient.invalidateQueries({
            queryKey: commonQueryKeys.userNames(appId, envId, userIds),
          });
        }

        if (userIds.length === 0) setListType("all");
        onChangeProp(userIds);
      },
      [appId, envId, onChangeProp, queryClient, value],
    );

    return (
      <MultiSelectorPopover
        allowManualEntry={!isReadOnly}
        allowReadOnlyOpen={allowReadOnlyOpen}
        allSelectedToggle={!isReadOnly}
        addRawLabel={(searchValue) => (
          <>
            Add user ID{" "}
            <Text as="span" fontWeight="bold">
              {searchValue}
            </Text>
          </>
        )}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        isLoadingSuggestions={isFetching}
        isReadOnly={isReadOnly}
        itemRenderFn={(item, search) => (
          <UserDisplay search={search} user={item} showEmail showId />
        )}
        listType={listType}
        noItemsFound="No users found"
        searchPlaceholder="Search users or enter user ID"
        searchValue={searchValue}
        suggestions={suggestions}
        value={value}
        onChange={onChange}
        onListTypeChange={setListType}
        onLoadMore={fetchNextPage}
        onSearchInput={setSearchValue}
      >
        {summary ? (
          <RuleSimpleSelection
            isLoading={isNamesLoading}
            readOnly={isReadOnly}
            type="user"
            isSummary
          >
            <GroupSummary
              avatarBackgroundColor={avatarBackgroundColor}
              count={summary.count}
              entityType="user"
              sampleEntities={summary.samples}
            />
          </RuleSimpleSelection>
        ) : (
          userNames.map((user) => (
            <RuleSimpleSelection
              key={user.id}
              isLoading={isNamesLoading}
              readOnly={isReadOnly}
              type="user"
              onRemove={() => {
                if (!value) return;
                const newValue = value.filter((id) => id !== user.id);
                onChange?.(newValue);
              }}
            >
              <UserDisplay user={user} />
            </RuleSimpleSelection>
          ))
        )}
      </MultiSelectorPopover>
    );
  },
);
RuleSimpleUserSelector.displayName = "RuleSimpleUserSelector";
